'use client';

import posthog from 'posthog-js';

// Track which errors have been captured to avoid duplicates
const capturedErrors = new WeakSet<Error>();

// Export function to mark errors as captured (for error boundaries)
export function markErrorAsCaptured(error: Error) {
  capturedErrors.add(error);
}

export function setupErrorTracking() {
  if (typeof window === 'undefined') return;

  // Capture unhandled errors
  window.addEventListener('error', event => {
    // Skip if this error was already captured by an error boundary
    if (event.error && capturedErrors.has(event.error)) {
      return;
    }
    const errorInfo = {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      type: 'window_error',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      // Additional context for better debugging
      errorName: event.error?.name,
      errorCause: event.error?.cause,
      // Include source information
      source: event.filename
        ? `${event.filename}:${event.lineno}:${event.colno}`
        : undefined,
      // Browser info
      browserName:
        navigator.userAgent.match(/(firefox|chrome|safari|edge|opera)/i)?.[0] ||
        'unknown',
      // Page visibility state
      visibilityState: document.visibilityState
    };

    // Only send if we have meaningful error info
    if (errorInfo.message || errorInfo.stack) {
      posthog.capture('$exception', errorInfo);
      // Mark this error as captured
      if (event.error) {
        capturedErrors.add(event.error);
      }
    }
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    const errorInfo = {
      message: reason?.message || String(reason),
      stack: reason?.stack,
      type: 'unhandled_promise_rejection',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      // Additional context
      errorName: reason?.name,
      errorCause: reason?.cause,
      promiseReason: String(reason),
      // Browser info
      browserName:
        navigator.userAgent.match(/(firefox|chrome|safari|edge|opera)/i)?.[0] ||
        'unknown',
      // Page visibility state
      visibilityState: document.visibilityState
    };

    // Only send if we have meaningful error info
    if (errorInfo.message || errorInfo.stack) {
      posthog.capture('$exception', errorInfo);
      // Mark this error as captured
      if (event.error) {
        capturedErrors.add(event.error);
      }
    }
  });
}
